import {
  type ApiHolder,
  type ConfigApi,
  configApiRef,
  type DiscoveryApi,
  discoveryApiRef,
  type FetchApi,
  fetchApiRef,
  type IdentityApi,
  identityApiRef,
  microsoftAuthApiRef,
  type OAuthApi,
  useApi,
} from '@backstage/core-plugin-api';
import type { CurrentUserLicense } from '@ns/plugin-ns-tessa-common/current-user-licenses';
import { loadCurrentUserLicenses } from '@ns/plugin-ns-tessa-common/current-user-licenses-loader';
import {
  QueryClient,
  type QueryFunctionContext,
  skipToken,
  useQuery,
} from '@tanstack/react-query';

/** The value `TessaUserLicensePicker` writes: the license type key and its display name. */
export interface TessaUserLicense {
  readonly id: string;
  readonly name: string;
}

export const TESSA_USER_LICENSES_UNAVAILABLE_MESSAGE =
  'License lookup is not available in this environment.';
export const TESSA_USER_LICENSES_FORBIDDEN_MESSAGE =
  'You have no access to your Tessa licenses.';
export const TESSA_USER_LICENSES_EMPTY_MESSAGE = 'You have no active licenses.';
export const TESSA_USER_LICENSES_FAILED_MESSAGE =
  'Could not load your Tessa licenses. Try again.';
export const TESSA_USER_LICENSES_BROKEN_MESSAGE =
  'Could not load your Tessa licenses. Contact the portal team.';

/** The Tessa license lookup is switched off in this environment. */
export class TessaUserLicensesUnavailableError extends Error {
  constructor() {
    super(TESSA_USER_LICENSES_UNAVAILABLE_MESSAGE);
    this.name = 'TessaUserLicensesUnavailableError';
  }
}

/** A Backstage API that the license lookup needs was not available. */
export class TessaUserLicensesTransportUnavailableError extends Error {
  /** @param apiName - The absent API, in words, such as `discovery API`. */
  constructor(apiName: string) {
    super(`Tessa license lookup requires the ${apiName}.`);
    this.name = 'TessaUserLicensesTransportUnavailableError';
  }
}

/** The Backstage APIs a license lookup travels through. */
export interface TessaUserLicensesTransport {
  readonly configApi: Pick<ConfigApi, 'getOptionalString'>;
  readonly discoveryApi: DiscoveryApi;
  readonly fetchApi: Pick<FetchApi, 'fetch'>;
  readonly identityApi: Pick<IdentityApi, 'getBackstageIdentity'>;
  readonly microsoftAuthApi: Pick<OAuthApi, 'getAccessToken'>;
}

/** The TanStack identity of the signed-in user's licenses. The user is part of it, so another user never sees this one's list. */
export type TessaUserLicensesQueryKey = readonly [
  'ns-tessa',
  'user-licenses',
  string | undefined,
];

/** The shared cache for the picker and its submit validator. */
export const tessaQueryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false, staleTime: 60_000 },
  },
});

/**
 * Maps licenses to the options the picker offers, one per license type key.
 *
 * @remarks
 * A user can hold several licenses of one type. The first one wins, and the
 * order of the input is kept.
 */
export function toLicenseOptions(
  licenses: readonly CurrentUserLicense[],
): TessaUserLicense[] {
  const options = new Map<string, TessaUserLicense>();
  for (const license of licenses) {
    if (!options.has(license.licenseTypeKey)) {
      options.set(license.licenseTypeKey, {
        id: license.licenseTypeKey,
        name: license.licenseTypeName,
      });
    }
  }
  return [...options.values()];
}

/** The TanStack key of one user's licenses. Without a user the key holds `undefined`, for a request that is not made yet. */
export function userLicensesQueryKey(
  userEntityRef: string | undefined,
): TessaUserLicensesQueryKey {
  return ['ns-tessa', 'user-licenses', userEntityRef];
}

/**
 * Builds the TanStack key and request for one user's licenses.
 *
 * @param userEntityRef - The signed-in user.
 * @param transport - How the request travels.
 */
export function buildUserLicensesQueryOptions(
  userEntityRef: string,
  transport: TessaUserLicensesTransport,
) {
  return {
    queryFn: async ({
      signal,
    }: QueryFunctionContext<TessaUserLicensesQueryKey>) => {
      // No `identityApi`: the user is part of the query key, so an answer is
      // only ever read for the user it was asked for.
      const { configApi, discoveryApi, fetchApi, microsoftAuthApi } = transport;
      const result = await loadCurrentUserLicenses(
        { configApi, discoveryApi, fetchApi, microsoftAuthApi },
        signal,
      );
      if (result.status === 'disabled') {
        throw new TessaUserLicensesUnavailableError();
      }
      return result.licenses;
    },
    queryKey: userLicensesQueryKey(userEntityRef),
  };
}

/** The TanStack identity of the signed-in user. */
export type SignedInUserQueryKey = readonly ['ns-tessa', 'signed-in-user'];

/**
 * Builds the TanStack key and request for the signed-in user's entity ref.
 *
 * @remarks
 * The answer never goes stale: signing out in Backstage reloads the page, and
 * with it this cache, so a page only ever has one user.
 */
export function buildSignedInUserQueryOptions(
  identityApi: TessaUserLicensesTransport['identityApi'],
) {
  const queryKey: SignedInUserQueryKey = ['ns-tessa', 'signed-in-user'];
  return {
    queryFn: async () => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      return userEntityRef;
    },
    queryKey,
    staleTime: Infinity,
  };
}

/** The lookup state `TessaUserLicensePicker` renders from. */
export interface UserLicensesLookup {
  readonly data: TessaUserLicense[] | undefined;
  readonly error: unknown;
  readonly isError: boolean;
  readonly isPending: boolean;
  readonly refetch: () => void;
  /** Names the question the list answers: the licenses of the signed-in user. Absent until the user is known. */
  readonly sourceKey: string | undefined;
}

/**
 * Looks up the signed-in user's licenses as the options the picker offers.
 *
 * @remarks
 * Two dependent queries in {@link tessaQueryClient}: the user first, because
 * it is part of the licenses key, then the licenses, skipped until the user is
 * known. A failure of either is the lookup's failure, and `refetch` retries
 * the one that failed.
 */
export function useUserLicenses(): UserLicensesLookup {
  const transport = {
    configApi: useApi(configApiRef),
    discoveryApi: useApi(discoveryApiRef),
    fetchApi: useApi(fetchApiRef),
    identityApi: useApi(identityApiRef),
    microsoftAuthApi: useApi(microsoftAuthApiRef),
  };
  const user = useQuery(
    buildSignedInUserQueryOptions(transport.identityApi),
    tessaQueryClient,
  );
  const userEntityRef = user.data;
  const queryKey = userLicensesQueryKey(userEntityRef);
  const licenses = useQuery(
    {
      queryFn:
        userEntityRef === undefined
          ? skipToken
          : buildUserLicensesQueryOptions(userEntityRef, transport).queryFn,
      queryKey,
      select: toLicenseOptions,
    },
    tessaQueryClient,
  );
  const failed = user.isError ? user : licenses;
  return {
    data: licenses.data,
    error: failed.error,
    isError: failed.isError,
    isPending: user.isPending || licenses.isFetching,
    refetch: () => void failed.refetch(),
    sourceKey:
      userEntityRef === undefined ? undefined : JSON.stringify(queryKey),
  };
}

/**
 * Reads the license transport from Backstage's APIs, for code outside React such as a submit validator.
 *
 * @throws {@link TessaUserLicensesTransportUnavailableError} when one of the five APIs is absent.
 */
export function readUserLicensesTransport(
  apiHolder: ApiHolder,
): TessaUserLicensesTransport {
  const configApi = apiHolder.get(configApiRef);
  const discoveryApi = apiHolder.get(discoveryApiRef);
  const fetchApi = apiHolder.get(fetchApiRef);
  const identityApi = apiHolder.get(identityApiRef);
  const microsoftAuthApi = apiHolder.get(microsoftAuthApiRef);
  if (configApi === undefined)
    throw new TessaUserLicensesTransportUnavailableError('configuration API');
  if (discoveryApi === undefined)
    throw new TessaUserLicensesTransportUnavailableError('discovery API');
  if (fetchApi === undefined)
    throw new TessaUserLicensesTransportUnavailableError('fetch API');
  if (identityApi === undefined)
    throw new TessaUserLicensesTransportUnavailableError('identity API');
  if (microsoftAuthApi === undefined)
    throw new TessaUserLicensesTransportUnavailableError(
      'Microsoft authentication API',
    );
  return { configApi, discoveryApi, fetchApi, identityApi, microsoftAuthApi };
}
